import type { Influencer, InfluencerLocation } from '@foodfluence/shared';
import { hasValidTikTokHandle } from '../../utils/influencerUtils';

export type ProfileStep = { label: string; section: string };

export type ProfileCompleteness = { done: number; total: number; next?: ProfileStep };

/** "unknown" is the placeholder signup writes; the profile treats it as empty. */
const isRealName = (v?: string) => !!v && v.trim() !== '' && v.trim().toLowerCase() !== 'unknown';

/**
 * Profile meter for the main Profile view. Order is the order the hint walks the creator
 * through; `section` is the existing editingSection key that fixes the step.
 */
export const getProfileCompleteness = (
  influencer: Partial<Influencer> | null | undefined,
  locations: InfluencerLocation[] | null | undefined,
  /** Live result of useInstagramVerificationStatus. Creators who verified at signup have no
   *  instagramVerifiedAt stamp; only their verification record says so. */
  isInstagramVerified?: boolean | null,
): ProfileCompleteness => {
  const i = influencer ?? {};
  const steps: (ProfileStep & { ok: boolean })[] = [
    // The photo is pulled from Instagram on verification, so Social accounts is where it gets fixed.
    { label: 'Profile photo', section: 'socials', ok: !!i.profile_pic_url },
    { label: 'Your name', section: 'personal-information', ok: isRealName(i.firstName) && isRealName(i.lastName) },
    { label: 'Verify Instagram', section: 'socials', ok: !!i.instagramVerifiedAt || isInstagramVerified === true },
    { label: 'TikTok handle', section: 'socials', ok: hasValidTikTokHandle(i) },
    { label: 'A location', section: 'locations', ok: (locations?.length ?? 0) > 0 },
    { label: 'Niches', section: 'niches', ok: (i.associatedTags?.length ?? 0) > 0 },
    {
      label: 'Rates',
      section: 'rates',
      ok:
        [
          i.rateRanges?.gifting,
          i.rateRanges?.instagramPosts,
          i.rateRanges?.instagramReels,
          i.rateRanges?.instagramStories,
          i.rateRanges?.tiktoks,
        ].some(r => (r?.minRate ?? 0) > 0) ||
        (i.ugcRateRanges ?? []).some(r => r.minRate > 0),
    },
    { label: 'Payout account', section: 'wallet', ok: !!i.whopCompanyId },
  ];
  const next = steps.find(s => !s.ok);
  return {
    done: steps.filter(s => s.ok).length,
    total: steps.length,
    next: next ? { label: next.label, section: next.section } : undefined,
  };
};
